export const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  const date =
    typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

export const formatTargetAudience = (audience) => ({
  all_users: 'All Users',
  new_users: 'New Users',
  premium_users: 'Premium Users',
  inactive_users: 'Inactive Users',
  VA: 'VA',
  custom: 'Custom Segment',
}[audience] || audience);

export const formatPlatform = (platform) => ({
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  email: 'Email',
}[platform] || platform);

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Timestamps reach the client in two shapes: values built in the browser are
// epoch seconds, while values read back from Postgres arrive as ISO strings.
// Everything downstream does arithmetic, so normalize to epoch seconds.
export const toEpochSeconds = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.floor(value) : null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
};

// Expiry runs from the moment a notification actually goes out, so a scheduled
// one still gets its full duration once it sends. A blank or non-positive
// duration means no expiry: clients treat a null expiry_time as open-ended,
// whereas 0 would expire the notification the instant it is created.
export const computeExpiryTime = (duration, sendTimeSeconds) => {
  const minutes = Number(duration);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const start = Number.isFinite(sendTimeSeconds)
    ? sendTimeSeconds
    : Math.floor(Date.now() / 1000);
  return Math.floor(start + minutes * 60);
};

// Returns null unless the form holds a complete, parseable schedule. The date
// and time inputs start blank when scheduling is switched on, and an unchecked
// NaN here reaches JSON serialization as an invalid Date and throws.
export const scheduledSendTimeFrom = (formData) => {
  if (!formData.isScheduled || !formData.scheduledDate || !formData.scheduledTime) {
    return null;
  }
  const sendTimeMs = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).getTime();
  return Number.isNaN(sendTimeMs) ? null : Math.floor(sendTimeMs / 1000);
};

// A null send time means "publish now" everywhere downstream, so a half-filled
// schedule has to block the submit instead of falling through to an immediate
// send that reaches every recipient.
export const scheduleValidationError = (formData) =>
  formData.isScheduled && scheduledSendTimeFrom(formData) === null
    ? 'Pick both a send date and time, or turn off "Schedule for later".'
    : null;

// Convert form data to notification object
export const formToNotification = (formData, existingNotification = null) => ({
  ...existingNotification,
  title: formData.title,
  notificationMessage: formData.notificationMessage,
  targetAudience: formData.targetAudience,
  urgency: formData.urgency,
  platforms: formData.platforms,
  duration: formData.duration,
  scheduledSendTime: formData.isScheduled ? 
    new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).getTime() / 1000 : null,
});

const pad = (value) => String(value).padStart(2, '0');

// The date and time inputs are local-time, and scheduledSendTimeFrom parses
// them back as local time, so both halves must be rendered in local time or the
// round-trip shifts the schedule by the UTC offset.
const toLocalDateInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toLocalTimeInput = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// Convert notification to form data for editing
export const notificationToForm = (notification) => {
  const scheduledSendTime = toEpochSeconds(notification.scheduledSendTime);
  const scheduledAt = scheduledSendTime == null ? null : new Date(scheduledSendTime * 1000);

  return {
    title: notification.title || '',
    notificationMessage: notification.notificationMessage || '',
    targetAudience: notification.targetAudience || 'all_users',
    urgency: notification.urgency || 'low',
    platforms: notification.platforms || [],
    duration: notification.duration ?? 0,
    isScheduled: scheduledAt !== null,
    scheduledDate: scheduledAt ? toLocalDateInput(scheduledAt) : '',
    scheduledTime: scheduledAt ? toLocalTimeInput(scheduledAt) : '',
  };
};
